'use client';

import React from 'react';
import styled from 'styled-components';
import { UptimeBar, SegmentData } from '@/components/charts';
import { GlobeIcon, LockIcon as LockIconComponent } from '@/components/icons/MonitorIcons';

type StatusCardVariant = 'status' | 'check' | 'uptime' | 'domain';

interface MonitorStatusCardProps {
  variant: StatusCardVariant;
  label: string;
  value?: string;
  subtitle?: string;
  uptimePercentage?: number;
  uptimeData?: Array<'up' | 'down' | 'paused' | SegmentData> | boolean[] | number[];
  locked?: boolean;
  domainExpiry?: string;
  sslExpiry?: string;
  className?: string;
}

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  text-transform: capitalize;
`;

const Value = styled.div<{ $status?: 'up' | 'down' | 'coming-soon' }>`
  font-size: 28px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${({ $status }) => {
    if ($status === 'up') return '#10b981';
    if ($status === 'down') return '#ef4444';
    return '#111827';
  }};
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-top: -4px;
`;

const UptimeSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
`;

const UptimePercentage = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
`;

const DomainSSLSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DomainItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const DomainIcon = styled.div<{ $color?: string }>`
  width: 20px;
  height: 20px;
  color: ${({ $color }) => $color || '#6b7280'};
  flex-shrink: 0;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const DomainInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DomainLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const DomainValue = styled.div<{ $color?: string }>`
  font-size: 16px;
  font-weight: 600;
  color: ${({ $color }) => $color || '#111827'};
`;

const LockSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LockItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LockIcon = styled.svg`
  width: 20px;
  height: 20px;
  color: #9ca3af;
`;

const LockText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const LockLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const UnlockButton = styled.button`
  background: transparent;
  border: none;
  color: #FB6F2A;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    text-decoration: underline;
  }
`;

/**
 * MonitorStatusCard Component
 * 
 * Displays various monitor status information
 */
export const MonitorStatusCard: React.FC<MonitorStatusCardProps> = ({
  variant,
  label,
  value,
  subtitle,
  uptimePercentage,
  uptimeData,
  locked,
  domainExpiry,
  sslExpiry,
  className,
}) => {
  const renderContent = () => {
    switch (variant) {
      case 'status':
        // Extract status from value (handles "🟢 up" or just "Up")
        const statusText = (value || '').toLowerCase();
        const detectedStatus = statusText.includes('up') ? 'up' : statusText.includes('down') ? 'down' : undefined;
        
        return (
          <>
            <Value $status={detectedStatus}>
              {value}
            </Value>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </>
        );

      case 'check':
        return (
          <>
            <Value $status="coming-soon">{value}</Value>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </>
        );

      case 'uptime':
        return (
          <>
            <UptimeSection>
              <UptimePercentage>{uptimePercentage}%</UptimePercentage>
            </UptimeSection>
            <UptimeBar uptime={uptimePercentage || 100} data={uptimeData} />
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </>
        );

      case 'domain':
        if (locked) {
          return (
            <LockSection>
              <LockItem>
                <LockIcon viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </LockIcon>
                <LockText>
                  <LockLabel>Domain valid until</LockLabel>
                  <UnlockButton>
                    <LockIcon viewBox="0 0 24 24" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
                    </LockIcon>
                    Unlock
                  </UnlockButton>
                </LockText>
              </LockItem>
              <LockItem>
                <LockIcon viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </LockIcon>
                <LockText>
                  <LockLabel>SSL certificate valid until</LockLabel>
                  <UnlockButton>
                    <LockIcon viewBox="0 0 24 24" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
                    </LockIcon>
                    Unlock
                  </UnlockButton>
                </LockText>
              </LockItem>
              <Subtitle style={{ marginTop: '8px', fontSize: '12px' }}>
                Available only in <strong>Solo</strong>, <strong>Team</strong> and <strong>Enterprise</strong>. <a href="#" style={{ color: '#10b981', fontWeight: 500 }}>Upgrade now</a>
              </Subtitle>
            </LockSection>
          );
        }
        
        // Unlocked state with domain and SSL data
        return (
          <DomainSSLSection>
            <DomainItem>
              <DomainIcon $color="#10b981">
                <GlobeIcon />
              </DomainIcon>
              <DomainInfo>
                <DomainLabel>Domain valid until</DomainLabel>
                <DomainValue $color="#10b981">{domainExpiry || 'N/A'}</DomainValue>
              </DomainInfo>
            </DomainItem>
            
            <DomainItem>
              <DomainIcon $color="#10b981">
                <LockIconComponent />
              </DomainIcon>
              <DomainInfo>
                <DomainLabel>SSL certificate valid until</DomainLabel>
                <DomainValue $color="#10b981">{sslExpiry || 'N/A'}</DomainValue>
              </DomainInfo>
            </DomainItem>
          </DomainSSLSection>
        );

      default:
        return <Value>{value}</Value>;
    }
  };

  return (
    <Card className={className}>
      <Label>{label}</Label>
      {renderContent()}
    </Card>
  );
};

export default MonitorStatusCard;

